let version = 1;
let request: IDBOpenDBRequest;
let db: IDBDatabase;

export interface User {
    id: string;
    name: string;
    email: string;
}

export enum Stores {
    ALLREFERENTIALS = 'AllReferentials',
    ALLREFERENTIALSIDS = 'AllReferentialsIds'
}

export enum EDBNames {
    REFERENTIALSDB = 'referentialsDB'
}

export const initDB = (dbName: EDBNames): Promise<boolean> => {
    return new Promise((resolve) => {
        request = indexedDB.open(dbName, 2);

        request.onupgradeneeded = (ev: any) => {
            db = ev?.target?.result;

            if (!db.objectStoreNames.contains(Stores.ALLREFERENTIALS)) {
                db.createObjectStore(Stores.ALLREFERENTIALS);
            }
            if (!db.objectStoreNames.contains(Stores.ALLREFERENTIALSIDS)) {
                db.createObjectStore(Stores.ALLREFERENTIALSIDS);
            }
        };

        request.onsuccess = (ev: any) => {
            db = ev?.target?.result;
            version = db.version;
            resolve(true);
        };

        request.onerror = () => {
            resolve(false);
        };
    });
};

export const clearData = <T>(storeName: string, dbName: EDBNames): Promise<T | boolean | string | null> => {
    return new Promise((resolve) => {
        request = indexedDB.open(dbName, version);

        request.onsuccess = (ev: any) => {
            db = ev?.target?.result;
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.clear();
            resolve(true);
        };

        request.onerror = () => {
            const error = request.error?.message;
            if (error) {
                resolve(error);
            } else {
                resolve('Unknown error');
            }
        };
    });
};

export const updateData = <T>(storeName: string, data: T, key: string, dbName: EDBNames): Promise<T | string | null> => {
    return new Promise((resolve) => {
        request = indexedDB.open(dbName, version);

        request.onsuccess = (ev: any) => {
            db = ev?.target?.result;
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.put(data, key);
            resolve(data);
        };

        request.onerror = () => {
            const error = request.error?.message;
            if (error) {
                resolve(error);
            } else {
                resolve('Unknown error');
            }
        };
    });
};

export const addData = <T>(storeName: string, data: T, key: string, dbName: EDBNames): Promise<T | string | null> => {
    return new Promise((resolve) => {
        request = indexedDB.open(dbName, version);

        request.onsuccess = (ev: any) => {
            db = ev?.target?.result;
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.add(data, key);
            resolve(data);
        };

        request.onerror = () => {
            const error = request.error?.message;
            if (error) {
                resolve(error);
            } else {
                resolve('Unknown error');
            }
        };
    });
};

export const getStoreData = <T>(storeName: Stores, dbName: EDBNames): Promise<T[]> => {
    return new Promise((resolve) => {
        request = indexedDB.open(dbName);

        request.onsuccess = (ev: any) => {
            db = ev?.target?.result;
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const res = store.getAll();
            res.onsuccess = () => {
                resolve(res.result);
            };
        };
    });
};

export const getStoreDataByKey = <T>(storeName: Stores, key: string): Promise<T> => {
    return new Promise((resolve) => {
        request = indexedDB.open('referentialsDB');

        request.onsuccess = (ev: any) => {
            db = ev?.target?.result;
            if (db.objectStoreNames.length === 0) {
                resolve(undefined);
            } else {
                const tx = db?.transaction(storeName, 'readonly');
                const store = tx?.objectStore(storeName);
                const res = store?.get(key);
                res.onsuccess = () => {
                    resolve(res.result);
                };
            }
        };
    });
};

export const removeIndexedDB = (dbName: string = EDBNames.REFERENTIALSDB): void => {
    console.log(`🔍 Attempting to delete database: "${dbName}"`);

    if (db && db.name === dbName) {
        try {
            db.close();
            db = null as any; // Clear the reference
            console.log(`🔒 Closed open connection to database "${dbName}".`);
        } catch (e) {
            console.warn(`⚠️ Failed to close connection:`, e);
        }
    }

    if (request) {
        request = null as any;
    }

    const performDelete = () => {
        const upgradeRequest = indexedDB.open(dbName, 999);

        upgradeRequest.onupgradeneeded = (event: any) => {
            const tempDb = event.target.result;
            const storeNames = Array.from(tempDb.objectStoreNames);
            storeNames.forEach(storeName => {
                tempDb.deleteObjectStore(storeName);
            });
            console.log(`🗑️ Deleted all stores from database "${dbName}".`);
        };

        upgradeRequest.onsuccess = () => {
            upgradeRequest.result.close();
            setTimeout(() => {
                const deleteRequest = indexedDB.deleteDatabase(dbName);

                deleteRequest.onsuccess = (event) => {
                    console.log(`✅ Database "${dbName}" has been successfully deleted.`);
                };

                deleteRequest.onerror = (event) => {
                    const error = deleteRequest.error?.message || deleteRequest.error?.name || 'Unknown error';
                    console.error(`❌ Error while deleting database "${dbName}":`, error);
                    if (deleteRequest.error) {
                        console.error('Error details:', deleteRequest.error);
                    }
                };

                deleteRequest.onblocked = (event) => {
                    console.warn(`⚠️ Deletion of database "${dbName}" is blocked. The database may be in use by another tab/window. Close all tabs and try again.`);
                };
            }, 100);
        };

        upgradeRequest.onerror = () => {
            const deleteRequest = indexedDB.deleteDatabase(dbName);

            deleteRequest.onsuccess = (event) => {
                console.log(`✅ Database "${dbName}" has been successfully deleted.`);
            };

            deleteRequest.onerror = (event) => {
                const error = deleteRequest.error?.message || deleteRequest.error?.name || 'Unknown error';
                console.error(`❌ Error while deleting database "${dbName}":`, error);
                if (deleteRequest.error) {
                    console.error('Error details:', deleteRequest.error);
                }
            };

            deleteRequest.onblocked = (event) => {
                console.warn(`⚠️ Deletion of database "${dbName}" is blocked. The database may be in use by another tab/window. Close all tabs and try again.`);
            };
        };
    };

    const openRequest = indexedDB.open(dbName);

    openRequest.onsuccess = () => {
        const tempDb = openRequest.result;
        tempDb.close();
        console.log(`🔒 Closed temporary connection to database "${dbName}".`);
        setTimeout(performDelete, 100);
    };

    openRequest.onerror = () => {
        console.log(`ℹ️ Cannot open database "${dbName}", attempting deletion...`);
        setTimeout(performDelete, 100);
    };

    openRequest.onupgradeneeded = () => {
        const tempDb = openRequest.result;
        tempDb.close();
        setTimeout(performDelete, 100);
    };
};